import { useState, useRef } from 'react';
/* import { MyFormData } from '../../../@/types'; */
import { ButtonPopup, ButtonSiguiente, Input } from '../../ui';
import Textarea from '../../ui/TextareaComun';
import { ContactBlockCss } from '../../styles';

// Props opcionales para el formulario
interface ContactFormProps {
  className?: string;
}

// Definición del estado del formulario
interface MyFormData {
  email: string; // Correo electrónico del usuario
  message: string; // Mensaje del usuario
  file: File | null; // Archivo adjunto (opcional)
}

// URL base del backend desde .env
import { safeFetch } from '@/api/safeFetch';


const ContactForm: React.FC<ContactFormProps> = () => {
  // Estado del formulario
  const [formData, setFormData] = useState<MyFormData>({
    email: '',
    message: '',
    file: null,
  });

  // Estado de envío, error y éxito
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Referencia al input de archivo para disparar el click programáticamente
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Maneja cambios en inputs y textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Maneja selección de archivo
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prevData) => ({
      ...prevData,
      file,
    }));
  };

  // Envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Crear FormData dinámicamente (no en el estado)
      const form = new FormData();
      if (formData.email) form.append('email', formData.email);
      if (formData.message) form.append('message', formData.message);
      if (formData.file) {
        form.append('file', formData.file);
       
      }

      // Enviar datos al backend
      const response = await safeFetch(`/api/contacto`, {
        method: 'POST',
        body: form,
      });

      // Manejo de errores de servidor
      if (!response.ok) {
        let errorMessage = 'Error al enviar el formulario.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = 'El servidor respondió de forma inesperada.';
        }
        throw new Error(errorMessage);
      }

      // Respuesta exitosa
     /*  const data = await response.json(); */
     

      setSuccessMessage('¡Formulario enviado con éxito!');
      // Reiniciar formulario
      setFormData({ email: '', message: '', file: null });
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Ocultar mensaje de éxito después de 3 segundos
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);

    } catch (err) {
      const errorMsg = (err as Error).message;
      setError(errorMsg);

      // Ocultar mensaje de error después de 3 segundos
      setTimeout(() => {
        setError(null);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`flex-1 h-auto max-h-[800px] bg-white text-white p-6 rounded-2xl shadow-2xl flex flex-col text-center justify-between ${ContactBlockCss.contacto}`}
      style={{ transform: "none" }}
    >
      {/* Mensajes de éxito o error */}
      <div className="relative">
        {(error || successMessage) && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 w-full text-center">
            {error && (
              <p className="bg-red-100 text-red-600 text-sm font-medium py-2 px-4 rounded-md inline-block shadow-lg">
                {error}
              </p>
            )}
            {successMessage && (
              <p className="bg-green-100 text-green-600 text-sm font-medium py-2 px-4 rounded-md inline-block shadow-lg">
                {successMessage}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Input de correo */}
      <div>
        <label className="block text-zinc-700 font-bold mb-2">Correo Electrónico</label>
        <Input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder='Pon aquí tu correo'
          className="w-full h-10 border rounded-md focus:outline-none focus:border-blue-500 text-zinc-900 bg-white"
          required
        />
      </div>

      {/* Textarea de mensaje */}
      <div>
        <label className="block text-zinc-700 font-bold my-2">Mensaje</label>
        <Textarea
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          className="w-full px-3 mb-4 border rounded-md focus:outline-none focus:border-blue-500 text-zinc-900"
          rows={4}
          required
        />
      </div>

      {/* Selector de archivo */}
      <div className="flex w-full items-center gap-2">
        {/* Botón para abrir selector de archivo */}
        <ButtonPopup
          type="button"
          className="w-3/5 h-8 px-2 hover:text-blue-600 shadow-none"
          onClick={() => fileInputRef.current?.click()}
        >
          {formData.file ? 'Cambiar' : 'Seleccionar'}
        </ButtonPopup>

        {/* Input file oculto */}
        <input
          type="file"
          name="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden w-full"
        />

        {/* Mostrar nombre del archivo seleccionado */}
        <Input
          type="text"
          value={formData.file ? formData.file.name : 'Sin archivos'}
          className="w-2/2 h-8 text-sm pl-2 dark:text-zinc-700 bg-zinc-200 shadow-none truncate border-none text-center"
          readOnly
        />
      </div>

      {/* Botón de enviar */}
      <ButtonSiguiente
        type="submit"
        className={`w-full font-bold mt-4 text-black bg-blue-500 rounded-md hover:bg-white hover:text-black transition duration-200 ${isSubmitting ? 'cursor-not-allowed' : ''}`}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Enviando...' : 'Enviar'}
      </ButtonSiguiente>
    </form>
  );
};

export default ContactForm;
